/**
 * InviteCodeSection - Display workspace invite code inline in Team Area
 *
 * Shows join code, copy/regenerate buttons, expiry countdown, tier-gating.
 */

import { useState, useEffect } from 'react';
import { onUserChange, getAuthHeader } from '../../lib/auth';
import { useWorkspaceTier } from '../../hooks/useWorkspaceTier';
import { useWorkspaceMembers } from '../../hooks';
import type { User } from 'firebase/auth';

interface InviteCodeSectionProps {
  workspaceId: string;
}

interface InviteData {
  url: string;
  code: string;
  expiresAt: number;
}

export function InviteCodeSection({ workspaceId }: InviteCodeSectionProps) {
  const [user, setUser] = useState<User | null>(null);
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // track:workspace-write-auth (#48220 Track A, Outcome 4) — see
  // HumanMembersSection.tsx's matching comment.
  const { tier } = useWorkspaceTier(workspaceId);
  const isFreeTier = tier === 'free';
  const { members } = useWorkspaceMembers(workspaceId);

  useEffect(() => {
    const unsubscribe = onUserChange((currentUser) => {
      setUser(currentUser);
    });
    return unsubscribe;
  }, []);

  const currentUserIsLead = !!user && members.some(
    (m) => m.user_id === user.uid && m.role === 'human_lead'
  );

  useEffect(() => {
    if (user && currentUserIsLead) {
      fetchInviteLink();
    }
  }, [user, currentUserIsLead]);

  async function fetchInviteLink() {
    setLoading(true);
    setError(null);

    try {
      // Read the stored link first; generate only when none exists or it
      // has expired, so opening Team doesn't invalidate a link already sent.
      const currentRes = await fetch('/api/invites/current', {
        headers: { ...(await getAuthHeader()) },
      });
      const current = await currentRes.json();
      if (!currentRes.ok) {
        setError(current.error || 'Failed to load invite link');
        return;
      }
      if (current.code && current.expiresAt > Date.now()) {
        setInviteData(current);
        return;
      }

      const response = await fetch('/api/invites/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeader()) },
        body: JSON.stringify({ workspaceId, userId: user?.uid }),
      });

      const result = await response.json();

      if (!response.ok) {
        setError(result.error || 'Failed to generate invite link');
        return;
      }

      setInviteData(result);
    } catch (err: any) {
      console.error('[InviteCodeSection] Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleRegenerate() {
    if (!confirm('This will invalidate the current invite link. Continue?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/invites/regenerate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeader()) },
        body: JSON.stringify({ workspaceId, userId: user?.uid }),
      });

      const result = await response.json();

      if (!response.ok) {
        setError(result.error || 'Failed to regenerate invite link');
        return;
      }

      setInviteData(result);
      setCopied(false);
    } catch (err: any) {
      console.error('[InviteCodeSection] Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleCopy() {
    if (!inviteData) return;

    navigator.clipboard.writeText(inviteData.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function formatExpiryDate(timestamp: number): string {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  function getExpiryStatus(expiresAt: number): 'neutral' | 'amber' | 'red' | 'expired' {
    const now = Date.now();
    const timeLeft = expiresAt - now;
    const daysLeft = timeLeft / (1000 * 60 * 60 * 24);

    if (timeLeft <= 0) return 'expired';
    if (daysLeft < 1) return 'red';
    if (daysLeft < 3) return 'amber';
    return 'neutral';
  }

  function getExpiryColor(status: 'neutral' | 'amber' | 'red' | 'expired'): string {
    switch (status) {
      case 'expired': return '#dc2626';
      case 'red': return '#dc2626';
      case 'amber': return '#f59e0b';
      case 'neutral': return '#6b7280';
    }
  }

  function getExpiryIcon(status: 'neutral' | 'amber' | 'red' | 'expired'): string {
    switch (status) {
      case 'expired': return '🔴';
      case 'red': return '🔴';
      case 'amber': return '🟡';
      case 'neutral': return '';
    }
  }

  function maskInviteCode(url: string): string {
    const codeMatch = url.match(/code=([^&]+)/);
    if (!codeMatch) return url;
    return url.replace(codeMatch[1], 'RELAY-●●●●');
  }

  if (!currentUserIsLead || (!inviteData && !loading && !error)) {
    return null;
  }

  const expiryStatus = inviteData ? getExpiryStatus(inviteData.expiresAt) : 'neutral';
  const expiryColor = getExpiryColor(expiryStatus);
  const expiryIcon = getExpiryIcon(expiryStatus);

  return (
    <section id="team-section-invite" style={{ marginBottom: 32 }}>
      <div className="sc-section-label">Workspace Invite Link</div>

      {loading && (
        <div style={{ padding: '1rem', color: 'var(--muted)', fontSize: 14, fontFamily: "var(--font-ui)" }}>
          Loading invite link...
        </div>
      )}

      {error && <div className="sc-callout error" style={{ marginBottom: 12 }}>{error}</div>}

      {inviteData && !loading && (
        <>
          {/* Invite Code Display */}
          <div
            className="sc-item-row"
            style={{
              marginBottom: 12,
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              wordBreak: 'break-all',
              color: 'var(--ink)',
              display: 'block',
            }}
          >
            {isFreeTier ? maskInviteCode(inviteData.url) : inviteData.url}
          </div>

          {/* Expiry Status */}
          <div style={{ fontSize: 13, color: expiryColor, marginBottom: 12, fontFamily: "var(--font-ui)" }}>
            {expiryIcon && `${expiryIcon} `}
            {expiryStatus === 'expired'
              ? 'Expired'
              : `Expires: ${formatExpiryDate(inviteData.expiresAt)}`}
          </div>

          {/* Free Tier Upgrade CTA */}
          {isFreeTier && (
            <div className="sc-callout warn" style={{ marginBottom: 12 }}>
              This workspace is on the Free plan which allows 1 human member. Upgrade to add collaborators.
            </div>
          )}

          {/* Action Buttons (hidden for Free tier) */}
          {!isFreeTier && (
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                className="btn sm"
                onClick={handleCopy}
                disabled={expiryStatus === 'expired' || loading}
                style={{
                  color: expiryStatus === 'expired' ? 'var(--muted)' : (copied ? '#0E9B6A' : undefined),
                  borderColor: expiryStatus === 'expired' ? 'var(--muted-light)' : undefined,
                }}
              >
                {copied ? '✓ Copied!' : 'Copy Link'}
              </button>

              <button className="btn sm danger" onClick={handleRegenerate} disabled={loading}>
                Regenerate
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
